import { Graph, G6Event } from '@antv/g6';
import throttle from 'lodash/throttle';

import noop from 'utils/noop';
import { GraphCommonEvent, ViewportChangeType, FlowchartEvent, GraphNodeEvent } from '../constants';
import { UnknownFunction, ViewPortEvent, IG6GraphEvent } from '../types';
import { normalizeNodeEventData } from './helpers';

export enum ListenerName {
  onNodeDblClick = 'onNodeDblClick',
  onZoom = 'onZoom',
  onNodeSelect = 'onNodeSelect',
}

type EventType = G6Event | FlowchartEvent | 'nodeselectchange';

interface ListenerConfig {
  name: string | ListenerName;
  g6EventName: EventType;
  getCallback: UnknownFunction;
}

interface ListenerConfigs {
  [key: string]: ListenerConfig;
}

export const listenerConfigs: ListenerConfigs = {
  [ListenerName.onNodeDblClick]: {
    name: ListenerName.onNodeDblClick,
    g6EventName: GraphNodeEvent.onNodeDoubleClick,
    getCallback:
      (fn: UnknownFunction = noop) =>
      (e: IG6GraphEvent) =>
        fn(normalizeNodeEventData(e)),
  },
  [ListenerName.onZoom]: {
    name: ListenerName.onZoom,
    g6EventName: GraphCommonEvent.onViewportChange,
    getCallback: (fn: UnknownFunction = noop) =>
      throttle((e: ViewPortEvent) => e.action === ViewportChangeType.Zoom && fn(e), 150),
  },
};

/**
 * Adds a listener to the graph using the listener config settings
 * @returns {function} A function which destroys the listener when called
 */
export const addListener = (
  graph: Graph,
  name: ListenerName | string,
  fn: UnknownFunction,
): (() => void) => {
  const { g6EventName, getCallback } = listenerConfigs[name] || {};

  if (!g6EventName || !getCallback) return noop;

  const cb = getCallback(fn);

  if (typeof cb !== 'function') return noop;

  // @ts-ignore
  graph?.on(g6EventName, cb);

  return () => graph?.off(g6EventName, cb);
};

/**
 * Adds a listener which reponds to the zoom of the mouse
 * ! This will not fire if the zoom is updated in any other way
 * @returns {function} A function which destroys the listener when called
 */
export const addMouseZoomListener = (graph: Graph, fn: UnknownFunction): (() => void) =>
  addListener(graph, ListenerName.onZoom, fn);

/**
 * A listener which fires on node double click
 * @returns {function} A function which destroys the listener when called
 */
export const addNodeDblClickListener = (graph: Graph, fn: UnknownFunction): (() => void) =>
  addListener(graph, ListenerName.onNodeDblClick, fn);
